<script setup lang="ts">
import { computed } from 'vue'
import type { BlogArticle } from '~/types/blog'

interface Props {
  articles: BlogArticle[]
  heading?: string
}

const props = withDefaults(defineProps<Props>(), {
  heading: 'Related articles',
})

function formatDate(isoDate: string): string {
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return 'Date unavailable'

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const normalizedArticles = computed(() =>
  props.articles.map((article) => ({
    ...article,
    href: `/blog/${article.slug}/`,
    publishedLabel: formatDate(article.publishedAt),
  })),
)
</script>

<template>
  <section
    v-if="normalizedArticles.length > 0"
    class="mt-12 rounded-2xl border border-spruce-100 bg-white p-6 shadow-sm"
    aria-label="Related articles"
  >
    <h2 class="text-xl font-semibold tracking-tight text-gray-900">
      {{ heading }}
    </h2>

    <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <article
        v-for="item in normalizedArticles"
        :key="item.path"
        class="group overflow-hidden rounded-xl border border-spruce-100 bg-white transition hover:-translate-y-1 hover:border-spruce-200 hover:shadow-md"
      >
        <NuxtLink
          :to="item.href"
          class="block"
        >
          <img
            v-if="item.heroImage"
            :src="item.heroImage"
            :alt="`Featured image for ${item.title}`"
            class="aspect-[16/9] w-full object-cover"
            loading="lazy"
          >
        </NuxtLink>

        <div class="space-y-2 p-4">
          <p class="text-xs font-medium text-gray-500">
            {{ item.publishedLabel }}
          </p>
          <h3 class="text-base font-semibold leading-tight text-gray-900">
            <NuxtLink
              :to="item.href"
              class="rounded-sm underline-offset-2 group-hover:text-spruce-800 group-hover:underline"
            >
              {{ item.title }}
            </NuxtLink>
          </h3>
        </div>
      </article>
    </div>
  </section>
</template>
